import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { loadObjections } from '../services/objectionService';
import { loadAuthSession } from '../auth';
import { objectionReplyWithAi } from '../services/aiFeatures';
import { useAiAction } from '../hooks/useAiAction';
import { AiSparkButton, AiErrorBanner, AiThinkingBar } from './AiAssist';

/* ── Persistence helpers ─────────────────────────────────────────────────── */
function branchKey(b) { return b ? b.replace(/[^a-z0-9]/gi, '_').toLowerCase() : '__all'; }
function orderKey(uid, b) { return `crm_obj_order_${uid || 'def'}_${branchKey(b)}`; }
function panelKey(uid)    { return `crm_obj_panel_${uid || 'def'}`; }

function loadOrder(uid, b) {
  try { const s = localStorage.getItem(orderKey(uid, b)); return s ? JSON.parse(s) : null; } catch { return null; }
}
function persistOrder(uid, b, ids) {
  try { localStorage.setItem(orderKey(uid, b), JSON.stringify(ids)); } catch {}
}
function loadPanel(uid) {
  try { const s = localStorage.getItem(panelKey(uid)); return s ? JSON.parse(s) : null; } catch { return null; }
}
function persistPanel(uid, state) {
  try { localStorage.setItem(panelKey(uid), JSON.stringify(state)); } catch {}
}

/* ── Constants ───────────────────────────────────────────────────────────── */
const MIN_W = 380;
const MIN_H = 260;
const DEF_W = 700;
const DEF_H = 540;

/* ── Dots drag handle icon ───────────────────────────────────────────────── */
function DragDots() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="#b0bec5" style={{ flexShrink: 0 }}>
      <circle cx="3" cy="2"  r="1.4"/><circle cx="7" cy="2"  r="1.4"/>
      <circle cx="3" cy="7"  r="1.4"/><circle cx="7" cy="7"  r="1.4"/>
      <circle cx="3" cy="12" r="1.4"/><circle cx="7" cy="12" r="1.4"/>
    </svg>
  );
}

/* ── Toggle switch ───────────────────────────────────────────────────────── */
function Toggle({ on, onChange, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      {label && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>{label}</span>}
      <button onClick={() => onChange(!on)}
        style={{ width: 32, height: 17, borderRadius: 9, border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0, background: on ? '#159c5f' : 'rgba(255,255,255,0.22)', position: 'relative', transition: 'background .18s' }}>
        <div style={{ position: 'absolute', top: 1.5, left: on ? 16 : 1.5, width: 14, height: 14, borderRadius: 7, background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
      </button>
    </div>
  );
}

/* ── Resize grip SVG ─────────────────────────────────────────────────────── */
function ResizeGrip() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
      <line x1="12" y1="4"  x2="4"  y2="12" stroke="#93a89a" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="12" y1="8"  x2="8"  y2="12" stroke="#93a89a" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="12" y1="12" x2="12" y2="12" stroke="#93a89a" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════════════════ */
export default function ObjectionPanel({
  branch,
  lead = null,
  callContext: callContextProp = '',
  initialX,
  initialY,
  initialW,
  initialH,
  forceInitialPosition = false,
  zIndex    = 402,
  preview   = false,
  objections: objProp = null,
}) {
  const userId = useMemo(() => loadAuthSession()?.userId || null, []);
  const saved  = useMemo(() => loadPanel(userId), [userId]);

  const [objections, setObjections] = useState(() => objProp || loadObjections());
  const [search, setSearch]         = useState('');
  const [expanded, setExpanded]     = useState(null);
  const [filterMode, setFilterMode] = useState('relevant');
  const [collapsed, setCollapsed]   = useState(false);
  const [copied, setCopied]         = useState(null);
  const [callContext, setCallContext] = useState(callContextProp || '');
  const [aiVariants, setAiVariants] = useState({});
  const [aiLoadingId, setAiLoadingId] = useState(null);
  const ai = useAiAction();
  const [dndMode, setDndMode]       = useState(() => saved?.dndMode ?? false);
  const [dragOverId, setDragOverId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const [pos,  setPos]  = useState({
    x: forceInitialPosition ? (initialX ?? 820) : (saved?.x ?? (initialX ?? 820)),
    y: forceInitialPosition ? (initialY ?? 80) : (saved?.y ?? (initialY ?? 80)),
  });
  const [size, setSize] = useState({
    w: forceInitialPosition ? (initialW ?? DEF_W) : (saved?.w ?? (initialW ?? DEF_W)),
    h: forceInitialPosition ? (initialH ?? DEF_H) : (saved?.h ?? (initialH ?? DEF_H)),
  });

  const [sortedIds, setSortedIds] = useState(() => {
    const ord = loadOrder(userId, branch);
    return ord ?? (objProp || loadObjections()).map(o => o.id);
  });

  const panelDrag = useRef(false);
  const panelOff  = useRef({ x: 0, y: 0 });
  const resizeRef = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => { setCallContext(callContextProp || ''); }, [callContextProp]);

  async function personalizeObjection(obj) {
    setAiLoadingId(obj.id);
    const result = await ai.run(() => objectionReplyWithAi({
      objectionTitle: obj.title,
      templateResponse: obj.response,
      leadName: lead?.name,
      company: lead?.company,
      branch: lead?.branch || branch,
      callContext: callContext.trim(),
    }));
    setAiLoadingId(null);
    if (result?.response) {
      setAiVariants(prev => ({ ...prev, [obj.id]: result.response }));
    }
  }

  /* ── Sync objProp ── */
  useEffect(() => {
    const base = objProp || loadObjections();
    setObjections(base);
    setSortedIds(prev => {
      const newIds = base.map(o => o.id);
      return [...prev.filter(id => newIds.includes(id)), ...newIds.filter(id => !prev.includes(id))];
    });
  }, [objProp]);

  /* ── Reload order when branch changes ── */
  useEffect(() => {
    const ord = loadOrder(userId, branch);
    if (ord) {
      setSortedIds(ord);
    } else {
      setSortedIds(objections.map(o => o.id));
    }
  }, [branch, userId]); // eslint-disable-line

  /* ── Debounced panel state persistence ── */
  useEffect(() => {
    if (preview) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persistPanel(userId, { x: pos.x, y: pos.y, w: size.w, h: size.h, dndMode });
    }, 400);
  }, [pos, size, dndMode, userId, preview]);

  /* ── Mouse events (drag + resize) ── */
  useEffect(() => {
    if (preview) return;
    function onMove(e) {
      if (panelDrag.current) {
        setPos({ x: e.clientX - panelOff.current.x, y: e.clientY - panelOff.current.y });
        return;
      }
      if (!resizeRef.current) return;
      const { dir, sx, sy, sw, sh, sl, st } = resizeRef.current;
      const dx = e.clientX - sx;
      const dy = e.clientY - sy;
      let nw = sw, nh = sh, nx = sl, ny = st;
      if (dir.includes('e')) nw = Math.max(MIN_W, sw + dx);
      if (dir.includes('w')) { nw = Math.max(MIN_W, sw - dx); nx = sl + sw - nw; }
      if (dir.includes('s')) nh = Math.max(MIN_H, sh + dy);
      if (dir.includes('n')) { nh = Math.max(MIN_H, sh - dy); ny = st + sh - nh; }
      setSize({ w: nw, h: nh });
      setPos({ x: nx, y: ny });
    }
    function onUp() { panelDrag.current = false; resizeRef.current = null; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [preview]);

  function startPanelDrag(e) {
    if (preview || e.target.closest('button, input, textarea, select')) return;
    panelDrag.current = true;
    panelOff.current  = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }

  function startResize(e, dir) {
    if (preview) return;
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { dir, sx: e.clientX, sy: e.clientY, sw: size.w, sh: size.h, sl: pos.x, st: pos.y };
  }

  /* ── Copy ── */
  function copyText(id, text) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 1600);
  }

  /* ── Relevance ── */
  const isRelevant = useCallback(obj =>
    obj.branches.length === 0 || (branch && obj.branches.includes(branch))
  , [branch]);

  /* ── Filter + sort ── */
  const filtered = objections.filter(obj => {
    if (filterMode === 'relevant' && !isRelevant(obj)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return obj.title.toLowerCase().includes(q) || obj.response.toLowerCase().includes(q);
    }
    return true;
  });

  const cards = [...filtered].sort((a, b) => {
    const ai = sortedIds.indexOf(a.id), bi = sortedIds.indexOf(b.id);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1; if (bi === -1) return -1;
    return ai - bi;
  });

  /* ── DnD reorder ── */
  function reorderCards(fromId, toId) {
    if (fromId === toId) return;
    setSortedIds(prev => {
      const next = [...prev];
      cards.forEach(o => { if (!next.includes(o.id)) next.push(o.id); });
      const fi = next.indexOf(fromId), ti = next.indexOf(toId);
      if (fi === -1 || ti === -1) return prev;
      next.splice(fi, 1); next.splice(ti, 0, fromId);
      persistOrder(userId, branch, next);
      return next;
    });
    setDragOverId(null); setDraggingId(null);
  }

  /* ── Responsive values ── */
  const cols  = !preview && size.w >= 560 ? 2 : 1;
  const fs    = preview ? 12 : size.w < 500 ? 11 : size.w < 660 ? 12 : 13;
  const rCnt  = objections.filter(isRelevant).length;

  /* ── Handle builder ── */
  const RH = ({ dir, style }) => (
    <div onMouseDown={e => startResize(e, dir)}
      style={{ position: 'absolute', zIndex: 12, cursor: `${dir}-resize`, ...style }} />
  );

  /* ── Styles ── */
  const outer = preview
    ? { width: '100%', position: 'relative', fontFamily: 'var(--font)' }
    : { position: 'fixed', left: pos.x, top: pos.y, zIndex, width: size.w, height: collapsed ? 'auto' : size.h, fontFamily: 'var(--font)' };

  const inner = {
    ...(preview ? {} : { position: 'absolute', inset: 0 }),
    width: preview ? '100%' : undefined,
    background: '#fff', borderRadius: 10,
    border: '1px solid #c8d6cb',
    boxShadow: preview ? '0 4px 20px rgba(15,30,60,0.1)' : '0 12px 48px rgba(15,30,60,0.2), 0 2px 8px rgba(15,30,60,0.08)',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    height: preview ? undefined : (collapsed ? 'auto' : '100%'),
  };

  /* ═════════════════════════════════════════════════════════════════════════ */
  return (
    <div style={outer}>

      {/* ── Resize handles ── */}
      {!preview && !collapsed && <>
        <RH dir="n"  style={{ top: -4,    left: 14,    right: 14,   height: 10 }} />
        <RH dir="s"  style={{ bottom: -4, left: 14,    right: 14,   height: 10 }} />
        <RH dir="e"  style={{ right: -4,  top: 14,     bottom: 14,  width: 10  }} />
        <RH dir="w"  style={{ left: -4,   top: 14,     bottom: 14,  width: 10  }} />
        <RH dir="ne" style={{ top: -4,    right: -4,   width: 16,   height: 16 }} />
        <RH dir="nw" style={{ top: -4,    left: -4,    width: 16,   height: 16 }} />
        <RH dir="se" style={{ bottom: -4, right: -4,   width: 16,   height: 16 }} />
        <RH dir="sw" style={{ bottom: -4, left: -4,    width: 16,   height: 16 }} />
      </>}

      {/* ── Inner panel ── */}
      <div style={inner}>

        {/* ── Header ── */}
        <div onMouseDown={startPanelDrag}
          style={{ background: '#17382a', padding: '9px 12px', cursor: preview ? 'default' : 'grab', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" style={{ flexShrink: 0 }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span style={{ fontSize: fs + 1, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>Einwandsbehandlung</span>
            {branch && (
              <span style={{ fontSize: Math.max(9, fs - 2), color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.1)', borderRadius: 4, padding: '2px 7px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
                {branch.split('/')[0].trim()}
              </span>
            )}
          </div>
          {!preview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              <Toggle on={dndMode} onChange={setDndMode} label="Sortieren" />
              <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.2)' }} />
              <button onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Aufklappen' : 'Minimieren'}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 5, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.75)' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  {collapsed ? <polyline points="6 9 12 15 18 9"/> : <polyline points="18 15 12 9 6 15"/>}
                </svg>
              </button>
            </div>
          )}
        </div>

        {!collapsed && (
          <>
            {/* ── Toolbar ── */}
            <div style={{ padding: '9px 12px 0', borderBottom: '1px solid #eaf0f7', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 5, marginBottom: 8 }}>
                {[{ id: 'relevant', label: `Passend  ${rCnt}` }, { id: 'all', label: `Alle  ${objections.length}` }].map(tab => (
                  <button key={tab.id} onClick={() => setFilterMode(tab.id)} style={{
                    flex: 1, height: 27, borderRadius: 6,
                    border: `1.5px solid ${filterMode === tab.id ? 'rgba(15,138,83,0.35)' : '#deeae0'}`,
                    background: filterMode === tab.id ? 'rgba(15,138,83,0.07)' : '#f7faf7',
                    color: filterMode === tab.id ? '#0d7647' : '#5e7a68',
                    fontSize: Math.max(10, fs - 1), fontWeight: filterMode === tab.id ? 600 : 400,
                    cursor: 'pointer', transition: 'all .12s',
                  }}>{tab.label}</button>
                ))}
              </div>
              <div style={{ position: 'relative', marginBottom: dndMode ? 6 : 9 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93a89a" strokeWidth="2"
                  style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Einwand suchen…"
                  style={{ width: '100%', height: 30, borderRadius: 7, border: '1.5px solid #deeae0', padding: `0 10px 0 28px`, fontSize: Math.max(10, fs - 1), outline: 'none', background: '#fff', color: '#17382a', boxSizing: 'border-box', transition: 'border-color .15s' }}
                  onFocus={e => e.target.style.borderColor = '#159c5f'}
                  onBlur={e => e.target.style.borderColor = '#deeae0'}
                />
              </div>
              {lead && !dndMode && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#66796c', marginBottom: 4 }}>Gesprächskontext (optional, für KI)</div>
                  <input value={callContext} onChange={e => setCallContext(e.target.value)} placeholder="z. B. GF im Termin, Interesse an Unterlagen…"
                    style={{ width: '100%', height: 28, borderRadius: 6, border: '1.5px solid #deeae0', padding: '0 8px', fontSize: 11, boxSizing: 'border-box' }}
                  />
                </div>
              )}
              <AiErrorBanner message={ai.error} onDismiss={ai.clearError} />
              {dndMode && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8, padding: '5px 8px', borderRadius: 6, background: 'rgba(29,190,116,0.06)', border: '1px dashed rgba(29,190,116,0.3)' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#159c5f" strokeWidth="2">
                    <polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/>
                    <polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/>
                    <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
                  </svg>
                  <span style={{ fontSize: Math.max(10, fs - 1), color: '#0f8a53', fontWeight: 500 }}>Karten ziehen zum Sortieren — Reihenfolge wird gespeichert</span>
                </div>
              )}
            </div>

            {/* ── Cards grid ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
              {cards.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '28px 0' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8d6cb" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 8px' }}>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <div style={{ fontSize: fs, fontWeight: 500, color: '#5e7a68' }}>Keine Einwände gefunden</div>
                  {filterMode === 'relevant' && (
                    <button onClick={() => setFilterMode('all')} style={{ marginTop: 6, fontSize: Math.max(10, fs - 1), color: '#159c5f', background: 'none', border: 'none', cursor: 'pointer' }}>
                      Alle anzeigen →
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 7, alignItems: 'start' }}>
                  {cards.map(obj => {
                    const isExp  = expanded === obj.id && !dndMode;
                    const isOver = dragOverId === obj.id && dndMode;
                    const isDrag = draggingId === obj.id;

                    return (
                      <div key={obj.id}
                        draggable={dndMode}
                        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', obj.id); setDraggingId(obj.id); }}
                        onDragEnd={() => { setDraggingId(null); setDragOverId(null); }}
                        onDragOver={e => { e.preventDefault(); setDragOverId(obj.id); }}
                        onDragLeave={() => setDragOverId(null)}
                        onDrop={e => { e.preventDefault(); reorderCards(e.dataTransfer.getData('text/plain'), obj.id); }}
                        style={{
                          borderRadius: 8,
                          border: `1.5px solid ${isOver ? '#159c5f' : isExp ? '#c4d4e8' : '#eaf0f7'}`,
                          background: isOver ? 'rgba(29,190,116,0.04)' : '#fff',
                          overflow: 'hidden',
                          boxShadow: isExp ? '0 4px 14px rgba(15,30,60,0.09)' : '0 1px 3px rgba(15,30,60,0.04)',
                          transition: 'border-color .12s, background .12s, box-shadow .12s, opacity .15s, transform .1s',
                          opacity: isDrag ? 0.4 : 1,
                          transform: isOver && !isDrag ? 'scale(1.015)' : 'scale(1)',
                          cursor: dndMode ? 'grab' : 'default',
                        }}>

                        {/* Title row */}
                        <button onClick={() => { if (!dndMode) setExpanded(isExp ? null : obj.id); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: `${fs < 12 ? 7 : 9}px 10px`, background: 'none', border: 'none', cursor: dndMode ? 'grab' : 'pointer', textAlign: 'left' }}>
                          {dndMode && <DragDots />}
                          <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: obj.color, flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: fs, fontWeight: 600, color: '#17382a', lineHeight: 1.3, wordBreak: 'break-word' }}>
                            {obj.title}
                          </span>
                          {obj.branches.length > 0 && isRelevant(obj) && (
                            <span style={{ fontSize: Math.max(9, fs - 2), color: obj.color, background: `${obj.color}18`, border: `1px solid ${obj.color}35`, borderRadius: 4, padding: '1px 5px', flexShrink: 0, lineHeight: 1.4 }}>
                              B
                            </span>
                          )}
                          {!dndMode && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#b0bec5" strokeWidth="2.5" style={{ flexShrink: 0, transition: 'transform .15s', transform: isExp ? 'rotate(180deg)' : 'none' }}>
                              <polyline points="6 9 12 15 18 9"/>
                            </svg>
                          )}
                        </button>

                        {/* Expanded response */}
                        {isExp && (
                          <div style={{ padding: `0 10px 10px 20px`, borderTop: '1px solid #f0f5f0' }}>
                            <p style={{ margin: '9px 0 0', fontSize: Math.max(10, fs - 1), lineHeight: 1.75, color: '#2d4a6b', fontStyle: 'italic' }}>
                              „{obj.response}"
                            </p>
                            {aiVariants[obj.id] && (
                              <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(117,118,201,0.06)', border: '1px solid rgba(117,118,201,0.2)' }}>
                                <div style={{ fontSize: Math.max(9, fs - 2), fontWeight: 700, color: '#4f4b9e', marginBottom: 4 }}>KI – personalisiert</div>
                                <p style={{ margin: 0, fontSize: Math.max(10, fs - 1), lineHeight: 1.7, color: '#17382a', fontStyle: 'italic' }}>„{aiVariants[obj.id]}"</p>
                                <button type="button" onClick={() => copyText(`ai-${obj.id}`, aiVariants[obj.id])} style={{ marginTop: 6, fontSize: 10, border: 'none', background: 'none', color: '#4f4b9e', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                                  {copied === `ai-${obj.id}` ? '✓ Kopiert' : 'KI-Antwort kopieren'}
                                </button>
                              </div>
                            )}
                            {aiLoadingId === obj.id && <AiThinkingBar label="KI passt Antwort an…" />}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {lead && (
                              <AiSparkButton small label="Anpassen" loading={aiLoadingId === obj.id} onClick={() => personalizeObjection(obj)} />
                            )}
                            <button onClick={() => copyText(obj.id, aiVariants[obj.id] || obj.response)} style={{
                              height: 25, padding: '0 9px', borderRadius: 6,
                              border: `1px solid ${copied === obj.id ? '#16a34a' : '#deeae0'}`,
                              background: copied === obj.id ? 'rgba(22,163,74,0.07)' : '#f7faf7',
                              color: copied === obj.id ? '#16a34a' : '#5e7a68',
                              fontSize: Math.max(10, fs - 2), fontWeight: 500, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5, transition: 'all .15s',
                            }}>
                              {copied === obj.id
                                ? <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>Kopiert!</>
                                : <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Kopieren</>
                              }
                            </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── SE resize grip indicator ── */}
            {!preview && (
              <div onMouseDown={e => startResize(e, 'se')}
                style={{ position: 'absolute', bottom: 5, right: 5, cursor: 'se-resize', opacity: 0.5, lineHeight: 0, zIndex: 5 }}>
                <ResizeGrip />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
